import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import API, { apis } from "common/apis";
import { logoutIfNeeded } from "common/utils";
import {
  getPersistedWhProcessingGigPayouts,
  persistWhProcessingGigPayouts,
  removePersistedWhProcessingGigPayouts,
} from "services/PersistService/PersistWhGigPayoutsService";
import {
  getPersistedWhProcessingEarningPayouts,
  persistWhProcessingEarningPayouts,
  removePersistedWhProcessingEarningPayouts,
} from "services/PersistService/PersistWhEarningPayoutsService";
import {
  getPersistedWhProcessingAlterEarnings,
  persistWhProcessingAlterEarnings,
  removePersistedWhProcessingAlterEarnings,
} from "services/PersistService/PersistWhAlterEarningsService";
import { RootState } from "store/store";

export interface IGigWorkerPayout {
  payoutId: number;
  userId: number;
  mysaPartnerId: string | null;
  payoutMonth: string;
  totalAmount: number;
  status: string;
  mysaExternalId: string;
  mysaRaisedOn: string | null;
}

export interface IGigWorkerEarning {
  userId: number;
  mysaAccountStatus: string;
  mysaPartnerId: string | null;
  name: string | null;
  mobile: string | null;
  earnings: number;
  earningDays: number;
  alteredEarnings: number | null;
  payableEarnings: number;
  paidAmount: number;
  pendingAmount: number;
}

export interface IGigPayoutsUpload {
  key?: string;
  headerMapping?: string;
  successRows?: any[];
  failedRows?: any[];
  timeUploaded?: string;
  [key: string]: any;
}

export interface WhPartnerServicesState {
  earnings: IGigWorkerEarning[];
  earningPayouts: IGigWorkerEarning[];
  payouts: IGigWorkerPayout[];
  processingGigPayouts: IGigPayoutsUpload;
  processingEarningPayouts: IGigPayoutsUpload;
  processingAlterEarnings: IGigPayoutsUpload;
  status: "loading" | "success" | "error" | "idle" | "cancelling" | "saving" | "downloading";
  payoutsStatus: "idle" | "loading" | "error" | "processing";
  earningPayoutsDownloadStatus: "idle" | "downloading" | "error";
  onboardingDownloadStatus: "idle" | "downloading" | "error";
  error?: string;
  payoutsError?: string;
}

const initialState: WhPartnerServicesState = {
  earnings: [],
  earningPayouts: [],
  payouts: [],
  processingGigPayouts: getPersistedWhProcessingGigPayouts(),
  processingEarningPayouts: getPersistedWhProcessingEarningPayouts(),
  processingAlterEarnings: getPersistedWhProcessingAlterEarnings(),
  status: "idle",
  payoutsStatus: "idle",
  earningPayoutsDownloadStatus: "idle",
  onboardingDownloadStatus: "idle",
};

export const downloadWhPartnerOnboarding = createAsyncThunk(
  "whPartnerServices/downloadOnboarding",
  async (_, thunkApi) => {
    try {
      const response = await API.get(apis.whPartnerOnboardingDownload);
      const downloadUrl: string = response.data?.url;
      if (!downloadUrl) throw new Error("Download URL not found in response");
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return true;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

export const saveWhProcessingGigPayouts = createAsyncThunk(
  "whPartnerServices/gigPayouts/save",
  async (_, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const { key } = state.whPartnerServices.processingGigPayouts;
    try {
      const response = await API.post(apis.whSavePartnerPayouts, {}, { params: { key } });
      return response.data;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

export const cancelWhProcessingGigPayouts = createAsyncThunk(
  "whPartnerServices/gigPayouts/cancel",
  async (_, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const { key } = state.whPartnerServices.processingGigPayouts;
    try {
      const response = await API.post(apis.whSavePartnerPayouts, {}, { params: { key, cancel: 1 } });
      return response.data;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

export const getWhPartnerPayouts = createAsyncThunk(
  "whPartnerServices/getPayouts",
  async (params: { month?: string; status?: string }, thunkApi) => {
    try {
      const response = await API.get(apis.whPartnerPayouts, { params });
      return response.data as IGigWorkerPayout[];
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

export const processWhPartnerPayouts = createAsyncThunk(
  "whPartnerServices/processPayouts",
  async (
    payload: { month: string; action: "PUSH" | "DISCARD"; payoutIds: number[] },
    thunkApi
  ) => {
    try {
      const response = await API.post(apis.whProcessPartnerPayouts, payload);
      return response.data;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

export const markSuccessWhPartnerPayouts = createAsyncThunk(
  "whPartnerServices/markSuccessPayouts",
  async (payload: { month: string; payoutIds: number[] }, thunkApi) => {
    try {
      const response = await API.post(apis.whMarkSuccessPartnerPayouts, payload);
      return response.data;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

export const getWhEarningPayouts = createAsyncThunk(
  "whPartnerServices/getEarningPayouts",
  async (params: { month?: string }, thunkApi) => {
    try {
      const response = await API.get(apis.whPartnerEarnings, { params });
      return response.data as IGigWorkerEarning[];
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

export const downloadWhEarningPayouts = createAsyncThunk(
  "whPartnerServices/downloadEarningPayouts",
  async (params: { month?: string }, thunkApi) => {
    try {
      const response = await API.get(apis.whPartnerEarningsDownload, { params });
      const downloadUrl: string = response.data?.url;
      if (!downloadUrl) throw new Error("Download URL not found in response");
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return true;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

export const saveWhProcessingEarningPayouts = createAsyncThunk(
  "whPartnerServices/earningPayouts/save",
  async (_, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const { key } = state.whPartnerServices.processingEarningPayouts;
    try {
      const response = await API.post(apis.whSavePartnerPayouts, {}, { params: { key } });
      return response.data;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

export const cancelWhProcessingEarningPayouts = createAsyncThunk(
  "whPartnerServices/earningPayouts/cancel",
  async (_, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const { key } = state.whPartnerServices.processingEarningPayouts;
    try {
      const response = await API.post(apis.whSavePartnerPayouts, {}, { params: { key, cancel: 1 } });
      return response.data;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

export const saveWhProcessingAlterEarnings = createAsyncThunk(
  "whPartnerServices/alterEarnings/save",
  async (_, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const { key } = state.whPartnerServices.processingAlterEarnings;
    try {
      const response = await API.post(apis.whSaveEarningsAlteration, {}, { params: { key } });
      return response.data;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

export const cancelWhProcessingAlterEarnings = createAsyncThunk(
  "whPartnerServices/alterEarnings/cancel",
  async (_, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const { key } = state.whPartnerServices.processingAlterEarnings;
    try {
      const response = await API.post(apis.whSaveEarningsAlteration, {}, { params: { key, cancel: 1 } });
      return response.data;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

const whPartnerServicesSlice = createSlice({
  name: "whPartnerServices",
  initialState,
  reducers: {
    setWhProcessingGigPayouts: (state, action) => {
      state.processingGigPayouts = { ...action.payload, timeUploaded: new Date().toISOString() };
      state.status = "idle";
      persistWhProcessingGigPayouts(state.processingGigPayouts);
    },
    removeWhProcessingGigPayouts: (state) => {
      state.processingGigPayouts = {};
      state.status = "idle";
      removePersistedWhProcessingGigPayouts();
    },
    setWhProcessingEarningPayouts: (state, action) => {
      state.processingEarningPayouts = { ...action.payload, timeUploaded: new Date().toISOString() };
      state.status = "idle";
      persistWhProcessingEarningPayouts(state.processingEarningPayouts);
    },
    removeWhProcessingEarningPayouts: (state) => {
      state.processingEarningPayouts = {};
      state.status = "idle";
      removePersistedWhProcessingEarningPayouts();
    },
    setWhProcessingAlterEarnings: (state, action) => {
      state.processingAlterEarnings = { ...action.payload, timeUploaded: new Date().toISOString() };
      state.status = "idle";
      persistWhProcessingAlterEarnings(state.processingAlterEarnings);
    },
    removeWhProcessingAlterEarnings: (state) => {
      state.processingAlterEarnings = {};
      state.status = "idle";
      removePersistedWhProcessingAlterEarnings();
    },
  },
  extraReducers(builder) {
    builder
      .addCase(downloadWhPartnerOnboarding.pending, (state) => {
        state.onboardingDownloadStatus = "downloading";
      })
      .addCase(downloadWhPartnerOnboarding.fulfilled, (state) => {
        state.onboardingDownloadStatus = "idle";
      })
      .addCase(downloadWhPartnerOnboarding.rejected, (state) => {
        state.onboardingDownloadStatus = "error";
      })

      .addCase(saveWhProcessingGigPayouts.pending, (state) => {
        state.status = "loading";
        state.error = undefined;
      })
      .addCase(saveWhProcessingGigPayouts.fulfilled, (state) => {
        state.status = "success";
        state.processingGigPayouts = {};
        removePersistedWhProcessingGigPayouts();
      })
      .addCase(saveWhProcessingGigPayouts.rejected, (state, action) => {
        state.status = "error";
        state.error = (action.payload as { message: string })?.message;
      })

      .addCase(cancelWhProcessingGigPayouts.pending, (state) => {
        state.status = "cancelling";
      })
      .addCase(cancelWhProcessingGigPayouts.fulfilled, (state) => {
        state.status = "idle";
        state.processingGigPayouts = {};
        removePersistedWhProcessingGigPayouts();
      })
      .addCase(cancelWhProcessingGigPayouts.rejected, (state, action) => {
        state.status = "error";
        state.error = (action.payload as { message: string })?.message;
      })

      .addCase(getWhEarningPayouts.pending, (state) => {
        state.status = "loading";
        state.error = undefined;
      })
      .addCase(getWhEarningPayouts.fulfilled, (state, action) => {
        state.status = "idle";
        state.earningPayouts = action.payload;
      })
      .addCase(getWhEarningPayouts.rejected, (state, action) => {
        state.status = "error";
        state.error = (action.payload as { message: string })?.message;
      })

      .addCase(downloadWhEarningPayouts.pending, (state) => {
        state.earningPayoutsDownloadStatus = "downloading";
      })
      .addCase(downloadWhEarningPayouts.fulfilled, (state) => {
        state.earningPayoutsDownloadStatus = "idle";
      })
      .addCase(downloadWhEarningPayouts.rejected, (state) => {
        state.earningPayoutsDownloadStatus = "error";
      })

      .addCase(saveWhProcessingEarningPayouts.pending, (state) => {
        state.status = "loading";
        state.error = undefined;
      })
      .addCase(saveWhProcessingEarningPayouts.fulfilled, (state) => {
        state.status = "success";
        state.processingEarningPayouts = {};
        removePersistedWhProcessingEarningPayouts();
      })
      .addCase(saveWhProcessingEarningPayouts.rejected, (state, action) => {
        state.status = "error";
        state.error = (action.payload as { message: string })?.message;
      })

      .addCase(cancelWhProcessingEarningPayouts.pending, (state) => {
        state.status = "cancelling";
      })
      .addCase(cancelWhProcessingEarningPayouts.fulfilled, (state) => {
        state.status = "idle";
        state.processingEarningPayouts = {};
        removePersistedWhProcessingEarningPayouts();
      })
      .addCase(cancelWhProcessingEarningPayouts.rejected, (state, action) => {
        state.status = "error";
        state.error = (action.payload as { message: string })?.message;
      })

      .addCase(getWhPartnerPayouts.pending, (state) => {
        state.payoutsStatus = "loading";
        state.payoutsError = undefined;
      })
      .addCase(getWhPartnerPayouts.fulfilled, (state, action) => {
        state.payoutsStatus = "idle";
        state.payouts = action.payload;
      })
      .addCase(getWhPartnerPayouts.rejected, (state, action) => {
        state.payoutsStatus = "error";
        state.payoutsError = (action.payload as { message: string })?.message;
      })

      .addCase(processWhPartnerPayouts.pending, (state) => {
        state.payoutsStatus = "processing";
        state.payoutsError = undefined;
      })
      .addCase(processWhPartnerPayouts.fulfilled, (state) => {
        state.payoutsStatus = "idle";
      })
      .addCase(processWhPartnerPayouts.rejected, (state, action) => {
        state.payoutsStatus = "error";
        state.payoutsError = (action.payload as { message: string })?.message;
      })

      .addCase(markSuccessWhPartnerPayouts.pending, (state) => {
        state.payoutsStatus = "processing";
        state.payoutsError = undefined;
      })
      .addCase(markSuccessWhPartnerPayouts.fulfilled, (state) => {
        state.payoutsStatus = "idle";
      })
      .addCase(markSuccessWhPartnerPayouts.rejected, (state, action) => {
        state.payoutsStatus = "error";
        state.payoutsError = (action.payload as { message: string })?.message;
      })

      .addCase(saveWhProcessingAlterEarnings.pending, (state) => {
        state.status = "loading";
        state.error = undefined;
      })
      .addCase(saveWhProcessingAlterEarnings.fulfilled, (state) => {
        state.status = "success";
        state.processingAlterEarnings = {};
        removePersistedWhProcessingAlterEarnings();
      })
      .addCase(saveWhProcessingAlterEarnings.rejected, (state, action) => {
        state.status = "error";
        state.error = (action.payload as { message: string })?.message;
      })

      .addCase(cancelWhProcessingAlterEarnings.pending, (state) => {
        state.status = "cancelling";
      })
      .addCase(cancelWhProcessingAlterEarnings.fulfilled, (state) => {
        state.status = "idle";
        state.processingAlterEarnings = {};
        removePersistedWhProcessingAlterEarnings();
      })
      .addCase(cancelWhProcessingAlterEarnings.rejected, (state, action) => {
        state.status = "error";
        state.error = (action.payload as { message: string })?.message;
      });
  },
});

export const {
  setWhProcessingGigPayouts,
  removeWhProcessingGigPayouts,
  setWhProcessingEarningPayouts,
  removeWhProcessingEarningPayouts,
  setWhProcessingAlterEarnings,
  removeWhProcessingAlterEarnings,
} = whPartnerServicesSlice.actions;
export default whPartnerServicesSlice.reducer;
